/**
 * Stripe Service for Exclusive Offers
 *
 * This service handles price retrieval for exclusive offers.
 *
 * FUTURE ENHANCEMENT:
 * To fetch prices dynamically from Stripe, you should:
 * 1. Create a Cloud Function that retrieves prices using Stripe secret key
 * 2. Call that function from `fetchPricesFromAPI()` below
 * 3. The Cloud Function should return prices in the format specified below
 *
 * For now, this service uses fallback prices that match current Stripe configuration.
 */

import { getExclusiveOffersConfig } from './stripeConfig';

/**
 * Price cache to avoid multiple calls
 */
let pricesCache = null;
let lastFetchTime = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

/**
 * Fallback prices based on current Stripe configuration (TEST mode)
 * These are the actual prices from the TEST Price IDs provided
 *
 * IMPORTANT: Update these when Stripe prices change
 */
const FALLBACK_PRICES = {
  'solo-discount': {
    amount: 2.50,
    currency: 'eur',
    recurring: {
      interval: 'month',
      interval_count: 1
    }
  },
  'multi-discount': {
    amount: 3.50,
    currency: 'eur',
    recurring: {
      interval: 'month',
      interval_count: 1
    }
  },
  'solo-lifetime': {
    amount: 30.00,
    currency: 'eur',
    type: 'one_time'
  },
  'multi-lifetime': {
    amount: 42.00,
    currency: 'eur',
    type: 'one_time'
  }
};

/**
 * Fetch prices from backend API (Cloud Function)
 *
 * TODO: Implement this function to call your Cloud Function
 * The Cloud Function should:
 * 1. Use Stripe secret key to fetch Price objects
 * 2. Return formatted price data
 *
 * Example Cloud Function endpoint: /api/exclusive-offers/prices
 */
async function fetchPricesFromAPI() {
  try {
    // TODO: Uncomment and implement when Cloud Function is ready
    // const response = await fetch('/api/exclusive-offers/prices');
    // const data = await response.json();
    // return data.prices;

    // For now, return fallback prices
    return null;
  } catch (error) {
    console.error('Error fetching prices from API:', error);
    return null;
  }
}

/**
 * Get prices with fallback
 */
async function getPricesWithFallback() {
  // Try to fetch from API first
  const apiPrices = await fetchPricesFromAPI();

  if (apiPrices) {
    return apiPrices;
  }

  // Use fallback prices
  return FALLBACK_PRICES;
}

/**
 * Get all exclusive offers with their prices
 *
 * @returns {Promise<Array>} Array of offers with prices
 */
export async function getExclusiveOffersPrices() {
  // Check cache
  if (pricesCache && lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)) {
    return pricesCache;
  }

  try {
    const config = getExclusiveOffersConfig();
    const prices = await getPricesWithFallback();

    const offers = Object.keys(config).map(offerKey => {
      const offerConfig = config[offerKey];
      const priceInfo = prices[offerKey];

      return {
        key: offerKey,
        ...offerConfig,
        price: priceInfo
      };
    });

    // Update cache
    pricesCache = offers;
    lastFetchTime = Date.now();

    return offers;
  } catch (error) {
    console.error('Error getting exclusive offers prices:', error);
    throw error;
  }
}

/**
 * Format price for display
 * @param {Object} priceInfo - Price information
 * @param {string} locale - Locale for formatting (default: 'fr-FR')
 */
export function formatPrice(priceInfo, locale = 'fr-FR') {
  if (!priceInfo) return '';

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: priceInfo.currency || 'eur',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formattedAmount = formatter.format(priceInfo.amount);

  // Add recurring info if applicable
  if (priceInfo.recurring) {
    const isFrench = locale.startsWith('fr');
    const interval = priceInfo.recurring.interval === 'month'
      ? (isFrench ? 'mois' : 'month')
      : (isFrench ? 'an' : 'year');
    return `${formattedAmount} / ${interval}`;
  }

  return formattedAmount;
}

/**
 * Get a single offer with price
 * @param {string} offerKey - The offer key
 */
export async function getOfferPrice(offerKey) {
  const offers = await getExclusiveOffersPrices();
  return offers.find(offer => offer.key === offerKey);
}

/**
 * Clear price cache (useful after price updates)
 */
export function clearPriceCache() {
  pricesCache = null;
  lastFetchTime = null;
}

export default {
  getExclusiveOffersPrices,
  getOfferPrice,
  formatPrice,
  clearPriceCache
};
